import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { useToast } from '../../hooks/useToast';
import { formatBytes } from '../../utils/helpers';
import { documentTypeServices } from '../../APIServices/documentTypeServices';
import { documentTagServices } from '../../APIServices/documentTagServices';
import { studyFolderAndDocumentServices } from '../../APIServices/studyFolderAndDocumentServices';

function DocumentUploadModal({ open, onClose, studyId, currentFolderId, folderOptions = [], onUploaded }) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [documentTypeList, setDocumentTypeList] = useState([]);
  const [documentTagList, setDocumentTagList] = useState([]);
  const [form, setForm] = useState({
    name: '',
    type: '',
    version: '1.0',
    folder: currentFolderId || '',
    status: 'review',
    confidentiality: 'Confidential',
    tags: []
  });
  const [pickedFile, setPickedFile] = useState(null);
  const fileInputRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const getSelectedValues = (event) => Array.from(event.target.selectedOptions || []).map((option) => option.value).filter(Boolean);

  useEffect(() => {
    if (currentFolderId) set('folder', currentFolderId);
  }, [currentFolderId]);

  useEffect(() => {
    const getMetadata = async () => {
      try {
        const [documentTypeData, documentTagData] = await Promise.all([
          documentTypeServices.getDocumentTypeData('active', ''),
          documentTagServices.getDocumentTagData('active', ''),
        ]);

        const typeRows = documentTypeData?.data || [];
        const tagRows = documentTagData?.data || [];

        setDocumentTypeList(typeRows);
        setDocumentTagList(tagRows);
      } catch (error) {
        setDocumentTypeList([]);
        setDocumentTagList([]);
      }
    };

    if (open) {
      getMetadata();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPickedFile(null);
      setForm({ name: '', type: '', version: '1.0', folder: currentFolderId || '', status: 'review', confidentiality: 'Confidential', tags: [] });
    }
  }, [open, currentFolderId]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickedFile(f);
    if (!form.name) set('name', f.name);
    const lower = f.name.toLowerCase();
    const matchedType = documentTypeList.find((item) => lower.includes(String(item.documentType || '').toLowerCase()));
    if (matchedType?.documentType) {
      set('type', matchedType.documentType);
    }
  };

  const submit = async () => {
    if (!form.name) {
      toast('Document name required', 'warn');
      return;
    }

    if (!form.folder) {
      toast('Please select a folder', 'warn');
      return;
    }

    if (!pickedFile) {
      toast('Please select a file to upload', 'warn');
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('studyId', studyId);
      payload.append('parentId', form.folder);
      payload.append('name', form.name);
      payload.append('code', form.name);
      payload.append('description', form.name);
      payload.append('type', 'file');
      payload.append('documentType', form.type || '');
      payload.append('version', form.version || '1.0');
      payload.append('confidentiality', form.confidentiality || 'Confidential');
      payload.append('documentTag', (Array.isArray(form.tags) ? form.tags : [form.tags]).filter(Boolean).join(','));
      payload.append('file', pickedFile);

      const data = await studyFolderAndDocumentServices.uploadStudyDocument(payload);
      if (data?.statusCode === 200) {
        toast(`${form.name} uploaded`, 'ok');
        onUploaded?.();
        onClose();
      }
    } catch (error) {
      toast('Unable to upload document.', 'warn');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload document"
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>
            <i className="ti ti-upload" /> Upload
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">File</label>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) {
              setPickedFile(f);
              if (!form.name) set('name', f.name);
            }
          }}
          style={{
            border: `1.5px dashed ${pickedFile ? 'var(--ok)' : 'var(--line-2)'}`,
            background: pickedFile ? 'var(--ok-soft)' : 'var(--surface-2)',
            borderRadius: 2,
            padding: '20px 18px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.15s, background 0.15s'
          }}
        >
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onPickFile} />
          {pickedFile ? (
            <div>
              <i className="ti ti-file-check" style={{ fontSize: 24, color: 'var(--ok)', display: 'block', marginBottom: 6 }} />
              <div className="medium sm">{pickedFile.name}</div>
              <div className="xs muted mt-1">{formatBytes(pickedFile.size)} · {pickedFile.type || 'unknown type'}</div>
              <div className="xs mt-2">
                <a style={{ color: 'var(--accent)' }} onClick={(e) => { e.stopPropagation(); setPickedFile(null); }}>
                  Remove
                </a>
              </div>
            </div>
          ) : (
            <div>
              <i className="ti ti-cloud-upload" style={{ fontSize: 26, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }} />
              <div className="sm medium">Click or drop a file here</div>
              <div className="xs muted mt-1">PDF, DOCX, XLSX and other allowed formats will upload to secure storage.</div>
            </div>
          )}
        </div>
      </div>

      <div className="field">
        <label className="field-label">File name <span className="req">*</span></label>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Protocol_v1.0.pdf" />
      </div>

      <div className="field-row">
        <div className="field">
          <label className="field-label">Document Type</label>
          <select value={form.type} onChange={(e) => set('type', e.target.value)}>
            <option value="">Select Document Type</option>
            {documentTypeList.map((item) => (
              <option key={item._id} value={item.documentType}>{item.documentType}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Version</label>
          <input value={form.version} onChange={(e) => set('version', e.target.value)} />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label className="field-label">Folder</label>
          <select value={form.folder} onChange={(e) => set('folder', e.target.value)}>
            <option value="">Select folder</option>
            {folderOptions.map((f) => (
              <option key={f.uniqueId} value={f.uniqueId}>{f.code}. {f.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Confidentiality</label>
          <select value={form.confidentiality} onChange={(e) => set('confidentiality', e.target.value)}>
            <option value="">Select Confidentiality</option>
            <option>Confidential</option>
            <option>Internal</option>
            <option>Public</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Document Tag</label>
        <select
          multiple
          value={Array.isArray(form.tags) ? form.tags : [form.tags].filter(Boolean)}
          onChange={(e) => set('tags', getSelectedValues(e))}
          style={{ minHeight: 120 }}
        >
          {documentTagList?.length === 0 ?
            <option value="">Select Document Tag</option>
            : documentTagList.map((item) => (
              <option key={item._id} value={item.documentTag}>{item.documentTag}</option>
            ))}
        </select>
        <div className="field-hint">No tag is selected by default. Hold Ctrl or Cmd to select multiple document tags.</div>
      </div>
    </Modal>
  );
}

export default DocumentUploadModal;